import { useEffect, useState } from "react";
import api, { formatErr } from "../../lib/api";
import { useRealtimeEvent } from "../../lib/realtime";
import { toast } from "sonner";
import { Plus, Trash2, UserCog, Ban, CheckCircle2, CornerDownRight, ShieldCheck, AlertCircle, Mail, Building2, MapPin, Phone, X, Download } from "@/components/icons";
import RowActionsMenu from "../../components/RowActionsMenu";
import Portal from "../../components/Portal";
import { LifetimeAiModal } from "../../components/admin/LifetimeAiModal";

const ROLES = [
  { v: "doctor", l: "Doctor" },
  { v: "secretary", l: "Secretaria" },
  { v: "pharmacy", l: "Farmacia" },
  { v: "admin", l: "Administrador" },
];

// Consideramos a un usuario "en línea" si tocó la API en los últimos 2 min.
const ONLINE_WINDOW_MS = 2 * 60 * 1000;
function isOnline(u, now = Date.now()) {
  if (!u?.last_seen_at) return false;
  const t = new Date(u.last_seen_at).getTime();
  return !Number.isNaN(t) && now - t < ONLINE_WINDOW_MS;
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [offices, setOffices] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [lifetimeFor, setLifetimeFor] = useState(null);
  const [officesModal, setOfficesModal] = useState(null); // { doctor, offices[] }
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "doctor",
    phone: "",
    address: "",
  });

  const load = async () => {
    const [u, o] = await Promise.all([api.get("/users"), api.get("/offices")]);
    setUsers(u.data);
    setOffices(o.data);
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [u, o] = await Promise.all([
          api.get("/users"),
          api.get("/offices"),
        ]);
        if (cancelled) return;
        setUsers(u.data);
        setOffices(o.data);
      } catch {
        /* ignore */
      }
    })();
    // Refresca el listado cada 30s para actualizar el indicador "En línea".
    const refreshInterval = setInterval(async () => {
      try {
        const u = await api.get("/users");
        if (!cancelled) setUsers(u.data);
      } catch {
        /* ignore */
      }
    }, 30000);
    return () => {
      cancelled = true;
      clearInterval(refreshInterval);
    };
  }, []);

  // Real-time: when any user touches the API and was previously offline,
  // the backend broadcasts `presence:update`. Merge into the local users
  // array so the chip flips on instantly.
  useRealtimeEvent("presence:update", (ev) => {
    if (!ev?.user_id) return;
    setUsers((prev) =>
      prev.map((u) =>
        u.id === ev.user_id ? { ...u, last_seen_at: ev.last_seen_at } : u,
      ),
    );
  });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("Usuario creado");
      setOpen(false);
      setForm({
        name: "",
        email: "",
        password: "",
        role: "doctor",
        phone: "",
        address: "",
      });
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const del = async (u) => {
    const sec = (secByDoctor[u.id] || []).length;
    const extra = sec
      ? `\n\nAdvertencia: también se eliminará${sec === 1 ? "" : "n"} ${sec} secretaria${sec === 1 ? "" : "s"} vinculada${sec === 1 ? "" : "s"}.`
      : "";
    if (!window.confirm(`¿Eliminar a ${u.name}?${extra}`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(
        sec
          ? `${u.name} y ${sec} secretaria(s) eliminada(s)`
          : "Usuario eliminado"
      );
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const [blockTarget, setBlockTarget] = useState(null);

  const toggleBlock = async (u) => {
    if (u.blocked) {
      const sec = (secByDoctor[u.id] || []).length;
      const extra = sec
        ? ` y a ${sec} secretaria${sec === 1 ? "" : "s"} vinculada${sec === 1 ? "" : "s"}`
        : "";
      if (!window.confirm(`¿Deseas desbloquear a ${u.name}${extra}?`)) return;
      try {
        await api.post(`/users/${u.id}/unblock`);
        toast.success(`${u.name}${extra} desbloqueado(s)`);
        load();
      } catch (err) {
        toast.error(formatErr(err.response?.data?.detail));
      }
      return;
    }
    // Open modal to ask for the reason
    setBlockTarget(u);
  };

  const confirmBlock = async (reason) => {
    if (!blockTarget) return;
    const u = blockTarget;
    const sec = (secByDoctor[u.id] || []).length;
    const extra = sec
      ? ` y a ${sec} secretaria${sec === 1 ? "" : "s"} vinculada${sec === 1 ? "" : "s"}`
      : "";
    try {
      await api.post(`/users/${u.id}/block`, { reason: reason || null });
      toast.success(`${u.name}${extra} bloqueado(s)`);
      setBlockTarget(null);
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const togglePharmacy = async (u) => {
    const next = !u.pharmacy_enabled;
    try {
      await api.put(`/admin/doctors/${u.id}/pharmacy`, {
        pharmacy_enabled: next,
      });
      toast.success(
        next
          ? `Envío a farmacia activado para ${u.name}`
          : `Envío a farmacia desactivado para ${u.name}`
      );
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const toggleAi = async (u) => {
    const next = !u.ai_enabled;
    try {
      await api.put(`/admin/doctors/${u.id}/ai`, { ai_enabled: next });
      toast.success(
        next
          ? `IA Aris activada para ${u.name}`
          : `IA Aris desactivada para ${u.name}`,
      );
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const sendVerification = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/send-verification`);
      if (data?.already_verified) {
        toast.info(`${u.name} ya está verificado`);
      } else {
        toast.success(`Correo de verificación enviado a ${data?.sent_to || u.email}`);
      }
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "No se pudo enviar el correo");
    }
  };

  // Admin always sees ALL users (including pharmacy-role accounts) and the
  // per-doctor "Farmacia" toggle column. The global `pharmacyEnabled` only
  // controls whether the module is hidden from PUBLIC pages (login/register
  // /onboarding) and from non-admin roles.
  const filtered = filter ? users.filter((u) => u.role === filter) : users;

  // Build a doctor → secretaries[] map using the offices table. A secretary
  // is linked to a doctor if she's assigned to one of his offices.
  const secByDoctor = {};
  // Set of doctor ids that have at least one office created — used to flag
  // doctors who self-registered but never completed onboarding.
  const doctorsWithOffice = new Set();
  // Map of doctor_id → offices[] used by the "Oficinas (N)" modal.
  const officesByDoctor = {};
  for (const o of offices) {
    if (o.doctor_id) {
      doctorsWithOffice.add(o.doctor_id);
      if (!officesByDoctor[o.doctor_id]) officesByDoctor[o.doctor_id] = [];
      officesByDoctor[o.doctor_id].push(o);
    }
    if (o.doctor_id && o.secretary_id) {
      if (!secByDoctor[o.doctor_id]) secByDoctor[o.doctor_id] = [];
      secByDoctor[o.doctor_id].push(o.secretary_id);
    }
  }
  // Build display rows: each doctor row is immediately followed by its
  // linked secretary rows (rendered indented). Secretaries already grouped
  // under a doctor are NOT shown again at the top level.
  const groupedSecretaryIds = new Set(
    Object.values(secByDoctor).flat()
  );
  const displayRows = [];
  for (const u of filtered) {
    if (u.role === "secretary" && groupedSecretaryIds.has(u.id) && !filter) {
      // Skip — will be rendered under its doctor below.
      continue;
    }
    displayRows.push({ ...u, _depth: 0 });
    if (u.role === "doctor" && !filter) {
      const secIds = secByDoctor[u.id] || [];
      for (const sid of secIds) {
        const s = users.find((x) => x.id === sid);
        if (s) displayRows.push({ ...s, _depth: 1, _parentName: u.name });
      }
    }
  }

  const initials = (name) =>
    (name || "")
      .split(" ")
      .map((s) => s[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase();

  const exportDoctor = async (u) => {
    setExporting(u ? u.id : "all");
    const t = toast.loading(u ? `Preparando datos de ${u.name}…` : "Preparando datos de todos los doctores…");
    try {
      const res = await api.get(u ? `/admin/export/doctors/${u.id}` : "/admin/export/doctors", { responseType: "blob" });
      const name = (res.headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || "ConsuDoctor.xlsx";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Descarga lista", { id: t });
    } catch (e) {
      toast.error("No pude generar el archivo", { id: t });
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-users">
      {lifetimeFor && <LifetimeAiModal doctor={lifetimeFor} onClose={() => setLifetimeFor(null)} onDone={load} />}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-black tracking-tight">
            Usuarios
          </h1>
          <p className="text-sm text-black/60">
            Crea y administra doctores, secretarias y farmacias.
          </p>
        </div>
        <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid="export-all-doctors"
          onClick={() => exportDoctor(null)}
          disabled={exporting === "all"}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-black/[0.05] text-black font-bold text-sm hover:bg-black/[0.09] transition disabled:opacity-50"
        >
          <Download className="w-4 h-4" /> {exporting === "all" ? "Preparando…" : "Descargar todo"}
        </button>
        <button
          data-testid="new-user-button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-black text-white font-bold text-sm hover:bg-black/85 transition"
        >
          <Plus className="w-4 h-4" /> Nuevo usuario
        </button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[{ v: "", l: "Todos" }, ...ROLES].map((r) => {
          const active = filter === r.v;
          return (
            <button
              key={r.v || "all"}
              onClick={() => setFilter(r.v)}
              className={`inline-flex items-center gap-2 pl-3 pr-4 py-2 rounded-full text-sm whitespace-nowrap transition border ${
                active
                  ? "bg-white border-[#E5E7EB] text-black font-extrabold shadow-sm"
                  : "bg-white/60 border-transparent text-black/55 font-bold hover:bg-white hover:text-black"
              }`}
            >
              <span
                className={`w-2.5 h-2.5 rounded-full ${
                  active ? "bg-[#01BC8E]" : "bg-black/25"
                }`}
              />
              {r.l}
            </button>
          );
        })}
      </div>

      {/* Users card with column-style table (like Doctor's home) */}
      <div className="bg-white rounded-[28px] border border-black/[0.06] p-6">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead>
              <tr className="text-left">
                {["Nombre", "Email", "Rol", "Teléfono", "Farmacia", ""].map(
                  (h, i) => (
                    <th
                      key={h || `c${i}`}
                      className="py-3 px-3 first:pl-0 last:pr-0 text-[11px] uppercase tracking-widest text-[#9CA3AF] font-medium"
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {displayRows.map((u) => (
                <tr
                  key={u.id}
                  data-testid={`user-row-${u.id}`}
                  className={`border-t border-[#E5E7EB] text-black transition ${
                    u.blocked
                      ? "bg-red-50/40 hover:bg-red-50/70"
                      : u._depth > 0
                      ? "bg-black/[0.02] hover:bg-black/[0.05]"
                      : "hover:bg-black/[0.03]"
                  }`}
                >
                  <td className="py-4 pr-3">
                    <div
                      className="flex items-center gap-3"
                      style={{ paddingLeft: u._depth ? "32px" : 0 }}
                    >
                      {u._depth > 0 && (
                        <CornerDownRight
                          className="w-3.5 h-3.5 text-black/30 shrink-0"
                          strokeWidth={2}
                        />
                      )}
                      <div className="relative w-9 h-9 flex-shrink-0">
                        <div className="absolute inset-0 rounded-full bg-[#DBEAFE] border border-[#E5E7EB] text-[#1B405B] text-xs font-extrabold flex items-center justify-center overflow-hidden">
                          {initials(u.name)}
                        </div>
                        {u.photo_url && u.photo_url.length > 100 && (
                          <img
                            src={u.photo_url}
                            alt=""
                            className="absolute inset-0 w-full h-full rounded-full object-cover"
                            onError={(e) => (e.currentTarget.style.display = "none")}
                          />
                        )}
                      </div>
                      <div className="flex flex-col min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`font-bold truncate max-w-[260px] ${u.blocked ? "text-black/55 line-through" : ""}`}>
                            {u.name}
                          </span>
                          {isOnline(u) && !u.blocked && (
                            <span
                              data-testid={`user-online-${u.id}`}
                              title="En línea ahora"
                              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#019A75] bg-blue-50 rounded-full px-1.5 py-0.5"
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-[#01BC8E] animate-pulse" />
                              En línea
                            </span>
                          )}
                          {u.role === "doctor" && !u.blocked && (
                            <button
                              type="button"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  const { data } = await api.patch(
                                    `/users/${u.id}/aris`,
                                    { enabled: !u.aris_enabled }
                                  );
                                  toast.success(
                                    data.aris_enabled
                                      ? "Aris habilitado"
                                      : "Aris deshabilitado"
                                  );
                                  load();
                                } catch (err) {
                                  toast.error(formatErr(err.response?.data?.detail));
                                }
                              }}
                              data-testid={`user-aris-toggle-${u.id}`}
                              title={
                                u.aris_enabled
                                  ? "Deshabilitar Aris"
                                  : "Habilitar Aris"
                              }
                              className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest rounded-full px-1.5 py-0.5 transition ${
                                u.aris_enabled
                                  ? "bg-[#01BC8E] text-white"
                                  : "bg-black/[0.06] text-black/60 hover:bg-black/[0.10]"
                              }`}
                            >
                              ✦ Aris {u.aris_enabled ? "ON" : "OFF"}
                            </button>
                          )}
                          {u.role === "doctor" && !u.blocked && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setLifetimeFor(u);
                              }}
                              data-testid={`user-ai-courtesy-${u.id}`}
                              title={u.ai_courtesy ? "Quitar IA de por vida" : "Activar IA de por vida"}
                              className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest rounded-full px-1.5 py-0.5 transition ${
                                u.ai_courtesy
                                  ? "bg-[#6FA2DE] text-white"
                                  : ["active", "trialing"].includes(u.ai_sub_status)
                                  ? "bg-[#6FA2DE]/15 text-[#3B6FB0]"
                                  : "bg-black/[0.06] text-black/60 hover:bg-black/[0.10]"
                              }`}
                            >
                              IA {u.ai_courtesy ? "De por vida" : ["active", "trialing"].includes(u.ai_sub_status) ? "Pagada" : "OFF"}
                            </button>
                          )}
                          {u.role === "doctor" && !u.blocked && (
                            u.email_verified ? (
                              <span
                                data-testid={`user-verified-${u.id}`}
                                title="Email verificado"
                                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-sky-700 bg-sky-100 rounded-full px-1.5 py-0.5"
                              >
                                <ShieldCheck className="w-3 h-3" />
                                Verificado
                              </span>
                            ) : (
                              <span
                                data-testid={`user-unverified-${u.id}`}
                                title="Email aún sin verificar"
                                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-amber-700 bg-amber-100 rounded-full px-1.5 py-0.5"
                              >
                                <AlertCircle className="w-3 h-3" />
                                Sin verificar
                              </span>
                            )
                          )}
                          {u.role === "doctor" && !u.blocked && !doctorsWithOffice.has(u.id) && (
                            <span
                              data-testid={`user-onboarding-pending-${u.id}`}
                              title="El doctor aún no ha creado su primera oficina"
                              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-indigo-700 bg-indigo-100 rounded-full px-1.5 py-0.5"
                            >
                              <Building2 className="w-3 h-3" />
                              Onboarding pendiente
                            </span>
                          )}
                          {u.role === "doctor" && (officesByDoctor[u.id]?.length || 0) > 0 && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOfficesModal({
                                  doctor: u,
                                  offices: officesByDoctor[u.id] || [],
                                });
                              }}
                              data-testid={`user-offices-button-${u.id}`}
                              title="Ver oficinas del doctor"
                              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-stone-700 bg-stone-100 hover:bg-stone-200 rounded-full px-1.5 py-0.5 transition"
                            >
                              <Building2 className="w-3 h-3" />
                              {officesByDoctor[u.id].length}{" "}
                              {officesByDoctor[u.id].length === 1 ? "oficina" : "oficinas"}
                            </button>
                          )}
                        </div>
                        {u.blocked && (
                          <>
                            <span className="text-[10px] font-bold text-red-700 uppercase tracking-widest">
                              Bloqueado
                            </span>
                            {u.block_reason && (
                              <span
                                className="text-[11px] text-red-700/85 mt-0.5 max-w-[260px] truncate"
                                title={u.block_reason}
                                data-testid={`block-reason-${u.id}`}
                              >
                                Motivo: {u.block_reason}
                              </span>
                            )}
                          </>
                        )}
                        {u._depth > 0 && u._parentName && !u.blocked && (
                          <span className="text-[10px] text-black/45">
                            vinculada a {u._parentName}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-3 text-black/85 truncate max-w-[260px]">
                    {u.email}
                  </td>
                  <td className="py-4 px-3">
                    <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-black/[0.06] text-black">
                      {ROLES.find((r) => r.v === u.role)?.l || u.role}
                    </span>
                  </td>
                  <td className="py-4 px-3 text-black/85 whitespace-nowrap">
                    {u.phone || "—"}
                  </td>
                  <td className="py-4 px-3">
                    {u.role === "doctor" ? (
                      <button
                        onClick={() => togglePharmacy(u)}
                        data-testid={`pharmacy-toggle-${u.id}`}
                        title={
                          u.pharmacy_enabled
                            ? "Desactivar envío a farmacia"
                            : "Activar envío a farmacia y notificar al doctor"
                        }
                        className={`relative w-11 h-6 rounded-full transition flex-shrink-0 ${
                          u.pharmacy_enabled ? "bg-[#01BC8E]" : "bg-black/20"
                        }`}
                      >
                        <span
                          className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${
                            u.pharmacy_enabled ? "left-6" : "left-1"
                          }`}
                        />
                      </button>
                    ) : (
                      <span className="text-black/30 text-xs">—</span>
                    )}
                  </td>
                  <td className="py-4 pl-3 text-right whitespace-nowrap">
                    {u._depth === 0 ? (
                      <RowActionsMenu
                        triggerTestId={`user-actions-${u.id}`}
                        items={[
                          ...(u.role === "doctor"
                            ? [
                                {
                                  icon: Download,
                                  label: "Descargar datos (Excel)",
                                  testId: `export-doctor-${u.id}`,
                                  onClick: () => exportDoctor(u),
                                  color: "sky",
                                },
                                { divider: true },
                              ]
                            : []),
                          ...(u.role === "doctor" && !u.email_verified && !u.blocked
                            ? [
                                {
                                  icon: Mail,
                                  label: "Reenviar verificación",
                                  testId: `send-verification-${u.id}`,
                                  onClick: () => sendVerification(u),
                                  color: "sky",
                                },
                                { divider: true },
                              ]
                            : []),
                          {
                            icon: u.blocked ? CheckCircle2 : Ban,
                            label: u.blocked ? "Desbloquear" : "Bloquear",
                            testId: `block-user-${u.id}`,
                            onClick: () => toggleBlock(u),
                            color: u.blocked ? "emerald" : "default",
                          },
                          { divider: true },
                          {
                            icon: Trash2,
                            label: "Eliminar",
                            testId: `delete-user-${u.id}`,
                            onClick: () => del(u),
                            color: "red",
                          },
                        ]}
                      />
                    ) : null}
                  </td>
                </tr>
              ))}
              {displayRows.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-black/55 py-10">
                    Sin usuarios
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <Portal>
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/40 backdrop-blur-md p-4 overflow-y-auto">
          <form
            onSubmit={submit}
            className="glass-modal rounded-3xl p-6 w-full max-w-lg space-y-3 my-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <UserCog className="w-5 h-5 text-[#1B405B]" />
              <h3 className="text-lg font-bold text-[#163144]">
                Nuevo usuario
              </h3>
            </div>
            <input
              required
              placeholder="Nombre completo"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full glass-input rounded-xl px-4 py-2.5"
              data-testid="user-name-input"
            />
            <input
              required
              type="email"
              placeholder="Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full glass-input rounded-xl px-4 py-2.5"
              data-testid="user-email-input"
            />
            <input
              required
              type="password"
              placeholder="Contraseña"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full glass-input rounded-xl px-4 py-2.5"
              data-testid="user-password-input"
            />
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="w-full glass-input rounded-xl px-4 py-2.5"
              data-testid="user-role-select"
            >
              {ROLES.map((r) => (
                <option key={r.v} value={r.v}>
                  {r.l}
                </option>
              ))}
            </select>
            <input
              placeholder="Teléfono (opcional)"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full glass-input rounded-xl px-4 py-2.5"
            />
            {form.role === "pharmacy" && (
              <input
                placeholder="Dirección de la farmacia"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="w-full glass-input rounded-xl px-4 py-2.5"
              />
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-5 py-2 rounded-full font-bold text-[#1B405B] hover:bg-[#DBEAFE]/50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn-primary"
                data-testid="user-submit-button"
              >
                Crear
              </button>
            </div>
          </form>
        </div>
        </Portal>
      )}

      {officesModal && (
        <OfficesModal
          doctor={officesModal.doctor}
          offices={officesModal.offices}
          secretariesById={Object.fromEntries(users.map((u) => [u.id, u]))}
          onClose={() => setOfficesModal(null)}
        />
      )}

      {blockTarget && (
        <BlockReasonModal
          user={blockTarget}
          onClose={() => setBlockTarget(null)}
          onConfirm={confirmBlock}
        />
      )}
    </div>
  );
}

function BlockReasonModal({ user, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onConfirm(reason.trim());
    } finally {
      setSaving(false);
    }
  };
  return (
    <Portal>
      <div
        className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
      >
        <form
          onSubmit={submit}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-[24px] p-6 w-full max-w-md space-y-4"
        >
          <div>
            <h3 className="text-lg font-extrabold text-black">
              Bloquear a {user.name}
            </h3>
            <p className="text-xs text-black/55 mt-1">
              Indica el motivo del bloqueo. El usuario no podrá iniciar sesión hasta
              que sea desbloqueado.
            </p>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-bold text-black/55">
              Motivo
            </label>
            <textarea
              autoFocus
              data-testid="block-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Ej. Falta de pago, comportamiento inapropiado..."
              className="mt-1 w-full bg-[#F5F5F5] rounded-2xl px-4 py-2.5 text-sm focus:outline-none border border-transparent focus:border-black/20"
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-black font-bold text-sm hover:bg-black/[0.05]"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="block-reason-submit"
              className="px-5 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-sm shadow disabled:opacity-60"
            >
              {saving ? "Bloqueando…" : "Bloquear"}
            </button>
          </div>
        </form>
      </div>
    </Portal>
  );
}

function OfficesModal({ doctor, offices, secretariesById, onClose }) {
  return (
    <Portal>
      <div
        className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/40 backdrop-blur-md p-4 overflow-y-auto"
        onClick={onClose}
        data-testid="offices-modal-overlay"
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-[28px] w-full max-w-2xl my-4 overflow-hidden"
          data-testid="offices-modal"
        >
          <div className="px-6 pt-6 pb-4 border-b border-black/[0.06]">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-widest font-bold text-black/45">
                  Oficinas
                </p>
                <h2 className="text-xl font-extrabold text-black mt-1">
                  {doctor?.name}
                </h2>
                <p className="text-xs text-black/55 mt-0.5">
                  {offices.length} {offices.length === 1 ? "consultorio" : "consultorios"} registrados
                </p>
              </div>
              <button
                onClick={onClose}
                data-testid="offices-modal-close"
                className="w-8 h-8 rounded-xl bg-black/[0.05] hover:bg-black/[0.1] flex items-center justify-center transition"
                aria-label="Cerrar"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <div className="px-6 py-5 space-y-3 max-h-[60vh] overflow-y-auto">
            {offices.length === 0 ? (
              <p className="text-sm text-black/55 text-center py-6">
                Este doctor aún no ha creado oficinas.
              </p>
            ) : (
              offices.map((o) => {
                const sec = o.secretary_id ? secretariesById[o.secretary_id] : null;
                return (
                  <div
                    key={o.id}
                    data-testid={`office-card-${o.id}`}
                    className="bg-[#F5F5F5] rounded-2xl p-4 flex gap-4"
                  >
                    <div className="w-14 h-14 rounded-2xl bg-white border border-black/[0.06] flex items-center justify-center overflow-hidden flex-shrink-0">
                      {o.logo_url ? (
                        <img
                          src={o.logo_url}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Building2 className="w-5 h-5 text-black/40" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="font-bold text-black">{o.name || "Sin nombre"}</div>
                      {o.address && (
                        <div className="text-[13px] text-black/65 flex items-start gap-1.5">
                          <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-black/40" />
                          <span className="truncate">{o.address}</span>
                        </div>
                      )}
                      {o.phone && (
                        <div className="text-[13px] text-black/65 flex items-center gap-1.5">
                          <Phone className="w-3.5 h-3.5 text-black/40" />
                          <a
                            href={`tel:${o.phone}`}
                            className="hover:text-black hover:underline"
                          >
                            {o.phone}
                          </a>
                        </div>
                      )}
                      {sec ? (
                        <div className="text-[12px] text-black/60 flex items-center gap-1.5 pt-1">
                          <CornerDownRight className="w-3 h-3 text-black/40" />
                          Secretaria:{" "}
                          <span className="font-semibold text-black">{sec.name}</span>
                          <span className="text-black/40">· {sec.email}</span>
                        </div>
                      ) : (
                        <div className="text-[11px] text-black/40 pt-1">
                          Sin secretaria vinculada
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </Portal>
  );
}
